import { createClient } from '@sanity/client'
import imageUrlBuilder from '@sanity/image-url'

export const client = createClient({
  projectId: 'a0mlvgoj',
  dataset: 'production',
  apiVersion: '2024-01-01',
  useCdn: true,
})

const builder = imageUrlBuilder(client)
export const urlFor = (source) => builder.image(source)

export const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  })
}

export const formatDateShort = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', year: 'numeric',
  })
}

// Fetch a single post by slug
export async function fetchPostBySlug(slug) {
  return client.fetch(
    `*[_type == "blogPost" && slug.current == $slug][0] {
      title, italicSubtitle, intro, "slug": slug.current,
      publishedAt, modifiedAt, readTime,
      heroImage, heroImageAlt, heroImageCaption, heroImageCredit,
      takeaways, body,
      seoTitle, seoDescription, seoCanonicalUrl, ogImage,
      keywords, articleSection, wordCount,
      sidebarStatNumber, sidebarStatNumberAccent, sidebarStatLabel,
      sidebarCtaEyebrow, sidebarCtaHeadline, sidebarCtaDescription,
      sidebarCtaButtonLabel, sidebarCtaButtonUrl,
      tags[]->{ title, "slug": slug.current },
      authorBioText,
      "category": category->{ title, "slug": slug.current, segment, badgeColor, badgeDotColor, badgeTextColor, sectionLabel },
      relatedPosts[]->{ title, "slug": slug.current, publishedAt, readTime, heroImage, heroImageAlt, "category": category->{ title, segment } },
      relatedSectionLabel
    }`,
    { slug }
  )
}

// Fetch all posts for blog index
export async function fetchAllPosts() {
  return client.fetch(
    `*[_type == "blogPost"] | order(publishedAt desc) {
      title, intro, "slug": slug.current, publishedAt, readTime,
      heroImage, heroImageAlt,
      "category": category->{ title, "slug": slug.current, segment, badgeColor, badgeDotColor, badgeTextColor },
      tags[]->{ title, "slug": slug.current }
    }`
  )
}

// Fetch posts by category (for related posts fallback)
export async function fetchRelatedPosts(categorySlug, currentSlug) {
  if (!categorySlug) return []
  return client.fetch(
    `*[_type == "blogPost" && category->slug.current == $categorySlug && slug.current != $currentSlug]
      | order(publishedAt desc)[0...3] {
      title, "slug": slug.current, publishedAt, readTime, heroImage, heroImageAlt,
      "category": category->{ title, segment }
    }`,
    { categorySlug, currentSlug }
  )
}

// Fetch all slugs (for any static generation needs)
export async function fetchAllSlugs() {
  return client.fetch(`*[_type == "blogPost"] { "slug": slug.current }`)
}

// Badge colors by segment (fallback if not set in Sanity)
export const SEGMENT_BADGE = {
  'photographers':   { bg: '#ede8f8', dot: '#6a52c0', text: '#3a2a8a' },
  'local-businesses':{ bg: '#e8f0e8', dot: '#4a8a4a', text: '#2a5a2a' },
  'real-estate':     { bg: '#e8eef8', dot: '#4a6ab0', text: '#2a4a8a' },
  'event-venues':    { bg: '#f8ede8', dot: '#b06a4a', text: '#8a3a2a' },
}

export const SEGMENT_GRADIENT = {
  'photographers':   'linear-gradient(135deg, #d8cce8 0%, #b8a8d0 100%)',
  'local-businesses':'linear-gradient(135deg, #c8e0c8 0%, #a8c8a8 100%)',
  'real-estate':     'linear-gradient(135deg, #c8d8f0 0%, #a8b8d8 100%)',
  'event-venues':    'linear-gradient(135deg, #f0d8c8 0%, #d8b8a8 100%)',
}

export function getBadgeColors(category) {
  const fallback = SEGMENT_BADGE[category?.segment] || SEGMENT_BADGE['photographers']
  return {
    bg:   category?.badgeColor    || fallback.bg,
    dot:  category?.badgeDotColor || fallback.dot,
    text: category?.badgeTextColor|| fallback.text,
  }
}

// Extract TOC from Portable Text body
export function extractToc(body) {
  if (!body) return []
  const toc = [{ text: 'Key Takeaways', id: 'key-takeaways', level: 'h2' }]
  body
    .filter(b => b._type === 'block' && ['h2', 'h3'].includes(b.style))
    .forEach(block => {
      const text = (block.children || []).map(c => c.text || '').join('')
      const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      toc.push({ text, id, level: block.style })
    })
  return toc
}

// Generate heading ID from text (must match extractToc)
export function slugifyHeading(text) {
  return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}
